/* eslint-env commonjs */

const DEFAULT_PROFILE_BY_BUCKET = {
  english: "openai-us-english",
  french: "openai-french-france",
  chinese: "openai-mainland-mandarin"
};

const VOICE_PROFILE_HINTS = {
  "openai-uk-female": {
    bucket: "english",
    fallbackLang: "en-GB",
    langPrefixes: ["en-gb"],
    preferredNames: [
      "Google UK English Female",
      "Moira",
      "Serena",
      "Kate",
      "Shelley (English (United Kingdom))",
      "Sandy (English (United Kingdom))",
      "Flo (English (United Kingdom))",
      "Eddy (English (United Kingdom))"
    ],
    preferredSearchPatterns: [/google uk english female/i],
    regionIncludePatterns: [/united kingdom|english \(united kingdom\)|british|\buk\b|england/i],
    regionExcludePatterns: [/united states|english \(united states\)|american|\bus\b/i],
    genderPatterns: [/female|woman|girl|moira|serena|kate|shelley|sandy|flo/i],
    genderExcludePatterns: [/male|man|boy|daniel|oliver|reed|rocko|grandpa/i]
  },
  "openai-uk-male": {
    bucket: "english",
    fallbackLang: "en-GB",
    langPrefixes: ["en-gb"],
    preferredNames: [
      "Google UK English Male",
      "Daniel",
      "Oliver",
      "Reed (English (United Kingdom))",
      "Rocko (English (United Kingdom))",
      "Grandpa (English (United Kingdom))",
      "Eddy (English (United Kingdom))"
    ],
    preferredSearchPatterns: [/google uk english male/i],
    regionIncludePatterns: [/united kingdom|english \(united kingdom\)|british|\buk\b|england/i],
    regionExcludePatterns: [/united states|english \(united states\)|american|\bus\b/i],
    genderPatterns: [/male|man|boy|daniel|oliver|reed|rocko|grandpa|eddy/i],
    genderExcludePatterns: [/female|woman|girl|moira|serena|kate|shelley|sandy|flo/i]
  },
  "openai-us-english": {
    bucket: "english",
    fallbackLang: "en-US",
    langPrefixes: ["en-us"],
    preferredNames: [
      "Google US English",
      "Samantha",
      "Alex",
      "Nicky",
      "Eddy (English (United States))",
      "Flo (English (United States))",
      "Shelley (English (United States))"
    ],
    preferredSearchPatterns: [/google us english/i],
    regionIncludePatterns: [/united states|english \(united states\)|american|\bus\b/i],
    regionExcludePatterns: [/united kingdom|english \(united kingdom\)|british|\buk\b|england/i],
    genderPatterns: []
  },
  "openai-french-france": {
    bucket: "french",
    fallbackLang: "fr-FR",
    langPrefixes: ["fr-fr"],
    preferredNames: [
      "Google français",
      "Thomas",
      "Jacques",
      "Flo (French (France))",
      "Shelley (French (France))",
      "Eddy (French (France))"
    ],
    preferredSearchPatterns: [/google fran[cç]ais$/i],
    regionIncludePatterns: [/france|french \(france\)|fr-fr/i],
    regionExcludePatterns: [/canada|quebec|fr-ca/i],
    genderPatterns: []
  },
  "openai-french-canada": {
    bucket: "french",
    fallbackLang: "fr-CA",
    langPrefixes: ["fr-ca"],
    preferredNames: [
      "Google français du Canada",
      "Amélie",
      "Flo (French (Canada))",
      "Shelley (French (Canada))",
      "Eddy (French (Canada))"
    ],
    preferredSearchPatterns: [/google fran[cç]ais du canada/i],
    regionIncludePatterns: [/canada|quebec|french \(canada\)|fr-ca/i],
    regionExcludePatterns: [/france|fr-fr/i],
    genderPatterns: []
  },
  "openai-taiwan-mandarin": {
    bucket: "chinese",
    fallbackLang: "zh-TW",
    langPrefixes: ["zh-tw", "cmn-tw"],
    preferredNames: [
      "Google 國語（臺灣）",
      "Meijia",
      "Flo (Chinese (Taiwan))",
      "Shelley (Chinese (Taiwan))",
      "Eddy (Chinese (Taiwan))",
      "Sandy (Chinese (Taiwan))"
    ],
    preferredSearchPatterns: [/google 國語（臺灣）/i, /google 國語 \(臺灣\)/i],
    regionIncludePatterns: [/taiwan|chinese \(taiwan\)|國語/i],
    regionExcludePatterns: [/china mainland|mainland|hong kong|cantonese|粤/i],
    genderPatterns: []
  },
  "openai-mainland-mandarin": {
    bucket: "chinese",
    fallbackLang: "zh-CN",
    langPrefixes: ["zh-cn", "cmn-cn", "cmn-hans-cn"],
    preferredNames: [
      "Google 普通话（中国大陆）",
      "Tingting",
      "Flo (Chinese (China mainland))",
      "Shelley (Chinese (China mainland))",
      "Eddy (Chinese (China mainland))",
      "Sandy (Chinese (China mainland))"
    ],
    preferredSearchPatterns: [/google 普通话（中国大陆）/i, /google 普通话 \(中国大陆\)/i],
    regionIncludePatterns: [/china mainland|mainland|putonghua|mandarin|普通话|普通話/i],
    regionExcludePatterns: [/taiwan|hong kong|cantonese|粤/i],
    genderPatterns: []
  },
  "openai-cantonese": {
    bucket: "chinese",
    fallbackLang: "yue-HK",
    langPrefixes: ["yue", "zh-hk"],
    preferredNames: ["Google 粤语（香港）", "Sinji"],
    preferredSearchPatterns: [/google 粤语（香港）/i, /google 粤语 \(香港\)/i],
    regionIncludePatterns: [/hong kong|cantonese|yue|粤/i],
    regionExcludePatterns: [/taiwan|mainland|mandarin|國語|普通话|普通話/i],
    genderPatterns: []
  }
};

function normalizeLookup(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeLang(value) {
  return normalizeLookup(value).replace(/_/g, "-");
}

function isChineseLanguage(language) {
  const normalized = normalizeLang(language);
  return (
    normalized.startsWith("zh") ||
    normalized.startsWith("cmn") ||
    normalized.startsWith("yue")
  );
}

function isFrenchLanguage(language) {
  return normalizeLang(language).startsWith("fr");
}

function getLanguageBucket(language) {
  if (isChineseLanguage(language)) {
    return "chinese";
  }

  if (isFrenchLanguage(language)) {
    return "french";
  }

  return "english";
}

function isNoveltyVoice(name) {
  return /bad news|bahh|bells|boing|bubbles|cellos|good news|jester|organ|superstar|trinoids|whisper|wobble|zarvox/i.test(
    String(name || "")
  );
}

function voiceMatchesLanguageBucket(voice, language) {
  const bucket = getLanguageBucket(language);
  const voiceLang = normalizeLang(voice?.lang);
  const voiceName = `${voice?.name || ""} ${voice?.voiceURI || ""}`.toLowerCase();

  if (bucket === "chinese") {
    return (
      voiceLang.startsWith("zh") ||
      voiceLang.startsWith("cmn") ||
      voiceLang.startsWith("yue") ||
      /chinese|mandarin|cantonese|zh|cmn|yue|taiwan|hong kong|粤|國語|普通话|普通話/.test(
        voiceName
      )
    );
  }

  if (bucket === "french") {
    return voiceLang.startsWith("fr") || /french|fran[cç]ais/.test(voiceName);
  }

  return voiceLang.startsWith("en") || /english|uk|british|american|us/.test(voiceName);
}

function hasPattern(patterns, value) {
  return Array.isArray(patterns) && patterns.some((pattern) => pattern.test(value));
}

function getProfileHints(selectedVoiceURI, language) {
  if (VOICE_PROFILE_HINTS[selectedVoiceURI]) {
    return VOICE_PROFILE_HINTS[selectedVoiceURI];
  }

  const bucket = getLanguageBucket(language);
  return VOICE_PROFILE_HINTS[DEFAULT_PROFILE_BY_BUCKET[bucket]];
}

function scoreBrowserVoice(voice, hints) {
  const normalizedName = normalizeLookup(voice?.name);
  const normalizedVoiceLang = normalizeLang(voice?.lang);
  const searchableName = `${voice?.name || ""} ${voice?.voiceURI || ""}`.toLowerCase();
  let score = 0;

  const preferredIndex = hints.preferredNames.findIndex(
    (preferredName) => normalizeLookup(preferredName) === normalizedName
  );

  if (preferredIndex !== -1) {
    score += 1000 - preferredIndex * 25;
  }

  if (hasPattern(hints.preferredSearchPatterns, searchableName)) {
    score += 880;
  }

  if (normalizedVoiceLang === normalizeLang(hints.fallbackLang)) {
    score += 320;
  } else if (hints.langPrefixes.some((prefix) => normalizedVoiceLang.startsWith(prefix))) {
    score += 280;
  } else if (
    hints.bucket === "english" &&
    normalizedVoiceLang.startsWith("en")
  ) {
    score += 120;
  } else if (
    hints.bucket === "french" &&
    normalizedVoiceLang.startsWith("fr")
  ) {
    score += 120;
  } else if (
    hints.bucket === "chinese" &&
    (normalizedVoiceLang.startsWith("zh") ||
      normalizedVoiceLang.startsWith("cmn") ||
      normalizedVoiceLang.startsWith("yue"))
  ) {
    score += 120;
  }

  if (hasPattern(hints.regionIncludePatterns, searchableName)) {
    score += 90;
  }

  if (hasPattern(hints.regionExcludePatterns, searchableName)) {
    score -= 140;
  }

  if (hasPattern(hints.genderPatterns, searchableName)) {
    score += 70;
  }

  if (hasPattern(hints.genderExcludePatterns, searchableName)) {
    score -= 170;
  }

  if (voice?.default) {
    score += 8;
  }

  if (isNoveltyVoice(voice?.name)) {
    score -= 320;
  }

  return score;
}

function resolveBrowserSpeechConfig({
  availableVoices,
  contentLanguage,
  selectedVoiceURI = ""
}) {
  const hints = getProfileHints(selectedVoiceURI, contentLanguage);
  const voices = Array.isArray(availableVoices) ? availableVoices.filter(Boolean) : [];

  if (!voices.length) {
    return {
      voice: null,
      utteranceLang: hints.fallbackLang
    };
  }

  const bucketMatches = voices.filter((voice) =>
    voiceMatchesLanguageBucket(voice, contentLanguage)
  );
  const candidates = bucketMatches.length ? bucketMatches : voices;
  let bestVoice = candidates[0] || null;
  let bestScore = Number.NEGATIVE_INFINITY;

  candidates.forEach((voice) => {
    const score = scoreBrowserVoice(voice, hints);

    if (score > bestScore) {
      bestScore = score;
      bestVoice = voice;
    }
  });

  return {
    voice: bestVoice,
    utteranceLang: normalizeLang(bestVoice?.lang) || hints.fallbackLang
  };
}

function getFallbackContinuationDelayMs(currentLanguage, nextLanguage) {
  return getLanguageBucket(currentLanguage) === getLanguageBucket(nextLanguage) ? 32 : 150;
}

function normalizeSpeechChunkText(text) {
  return String(text || "").replace(/\s+/g, " ").trim();
}

function createSpeechChunk(blocks, bucket, language) {
  return {
    bucket,
    language,
    text: "",
    textLength: 0,
    startBlockIndex: blocks[0]?.index ?? 0,
    endBlockIndex: blocks[blocks.length - 1]?.index ?? 0,
    blockRanges: []
  };
}

function buildSpeechChunks(blocks, options = {}) {
  const minChars = Math.max(120, Number(options.minChars) || 360);
  const targetChars = Math.max(minChars, Number(options.targetChars) || 820);
  const maxChars = Math.max(targetChars, Number(options.maxChars) || 1360);
  const maxBlocks = Math.max(1, Number(options.maxBlocks) || 5);
  const normalizedBlocks = Array.isArray(blocks)
    ? blocks
        .map((block, index) => {
          const text = normalizeSpeechChunkText(block?.text);

          if (!text) {
            return null;
          }

          return {
            index,
            language: block?.language || "en-US",
            bucket: getLanguageBucket(block?.language),
            text
          };
        })
        .filter(Boolean)
    : [];

  if (!normalizedBlocks.length) {
    return [];
  }

  const chunks = [];
  let currentChunk = null;

  const finalizeChunk = () => {
    if (!currentChunk || !currentChunk.textLength) {
      return;
    }

    chunks.push({
      ...currentChunk,
      blockRanges: currentChunk.blockRanges.map((range) => ({ ...range }))
    });
    currentChunk = null;
  };

  normalizedBlocks.forEach((block) => {
    const requiresNewChunk =
      !currentChunk ||
      currentChunk.bucket !== block.bucket ||
      currentChunk.language !== block.language;

    if (requiresNewChunk) {
      finalizeChunk();
      currentChunk = createSpeechChunk([block], block.bucket, block.language);
    }

    if (!currentChunk) {
      currentChunk = createSpeechChunk([block], block.bucket, block.language);
    }

    const separator = currentChunk.blockRanges.length ? "\n\n" : "";
    const prospectiveLength =
      currentChunk.textLength + separator.length + block.text.length;
    const shouldBreakBeforeAdding =
      currentChunk.blockRanges.length > 0 &&
      (
        currentChunk.blockRanges.length >= maxBlocks ||
        (
          prospectiveLength > maxChars &&
          (
            currentChunk.textLength >= minChars ||
            block.text.length >= targetChars * 0.85
          )
        )
      );

    if (shouldBreakBeforeAdding) {
      finalizeChunk();
      currentChunk = createSpeechChunk([block], block.bucket, block.language);
    }

    if (!currentChunk) {
      currentChunk = createSpeechChunk([block], block.bucket, block.language);
    }

    const currentSeparator = currentChunk.blockRanges.length ? "\n\n" : "";
    const startChar = currentChunk.textLength + currentSeparator.length;

    currentChunk.text += `${currentSeparator}${block.text}`;
    currentChunk.textLength = currentChunk.text.length;
    currentChunk.endBlockIndex = block.index;
    currentChunk.blockRanges.push({
      blockIndex: block.index,
      startChar,
      textLength: block.text.length
    });

    if (
      currentChunk.textLength >= targetChars ||
      currentChunk.blockRanges.length >= maxBlocks
    ) {
      finalizeChunk();
    }
  });

  finalizeChunk();

  return chunks.map((chunk, chunkIndex) => ({
    ...chunk,
    id: `speech-chunk-${chunkIndex}`,
    chunkIndex
  }));
}

function findSpeechChunkPosition(chunks, blockIndex, offsetChars = 0) {
  const normalizedChunks = Array.isArray(chunks) ? chunks : [];

  for (let chunkIndex = 0; chunkIndex < normalizedChunks.length; chunkIndex += 1) {
    const chunk = normalizedChunks[chunkIndex];

    if (blockIndex < chunk.startBlockIndex || blockIndex > chunk.endBlockIndex) {
      continue;
    }

    const range = chunk.blockRanges.find((item) => item.blockIndex === blockIndex)
      || chunk.blockRanges[0];
    const clampedOffset = Math.max(0, Math.min(Number(offsetChars) || 0, range.textLength));

    return {
      chunk,
      chunkIndex,
      startChar: range.startChar + clampedOffset
    };
  }

  const firstChunk = normalizedChunks[0] || null;

  return {
    chunk: firstChunk,
    chunkIndex: firstChunk ? 0 : -1,
    startChar: 0
  };
}

function mapSpeechChunkCharToCursor(chunk, fullCharIndex) {
  if (!chunk || !Array.isArray(chunk.blockRanges) || !chunk.blockRanges.length) {
    return {
      blockIndex: 0,
      offsetChars: 0
    };
  }

  const safeCharIndex = Math.max(
    0,
    Math.min(Number(fullCharIndex) || 0, Math.max(chunk.textLength - 1, 0))
  );

  for (let index = 0; index < chunk.blockRanges.length; index += 1) {
    const range = chunk.blockRanges[index];
    const nextRange = chunk.blockRanges[index + 1];
    const blockStart = range.startChar;
    const blockEnd = range.startChar + range.textLength;

    if (safeCharIndex < blockStart) {
      return {
        blockIndex: range.blockIndex,
        offsetChars: 0
      };
    }

    if (safeCharIndex <= blockEnd) {
      return {
        blockIndex: range.blockIndex,
        offsetChars: Math.max(0, Math.min(safeCharIndex - blockStart, range.textLength))
      };
    }

    if (nextRange && safeCharIndex < nextRange.startChar) {
      return {
        blockIndex: range.blockIndex,
        offsetChars: range.textLength
      };
    }
  }

  const lastRange = chunk.blockRanges[chunk.blockRanges.length - 1];

  return {
    blockIndex: lastRange.blockIndex,
    offsetChars: lastRange.textLength
  };
}

function shouldSuppressAiAudioForSession(error) {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
      ? error
      : JSON.stringify(error);

  return /insufficient_quota|rate.?limit|billing|quota|api key|temporarily unavailable|service unavailable|openai audio is not configured|server key|503/i.test(
    message || ""
  );
}

module.exports = {
  buildSpeechChunks,
  findSpeechChunkPosition,
  getFallbackContinuationDelayMs,
  mapSpeechChunkCharToCursor,
  resolveBrowserSpeechConfig,
  shouldSuppressAiAudioForSession
};
